// Extracted from chat/UXView.tsx so it can also be rendered inside AssessmentCaptureCard
// (WorkflowCard imports it too — UXView.tsx already imports WorkflowCard, so UXView could not be
// the source without a circular import). Behavior is unchanged from the original inline version.
import { useEffect, useRef, useState } from 'react';
import { useCreatePropertyNoteMutation } from '@/redux/internal-services/workspaceApiExtensions';
import { CardSection } from '@/components/UI/CardSection';
import { humanizeLabel, fmtDate } from '@/components/chat/ChatPills';

// 'measurement'/'inspection'/'site_observation' (VIN engagement-work taxonomy) are included
// alongside the PCR property-context types — without them there was no note_type a user could
// pick here that satisfies the assessment_note_required governance gate (required_note gate on
// complete_assessment checks note_type in these three, or context_type/tags — neither of which
// this simple form sets), so "Complete Assessment" was unsatisfiable from this panel. This is
// also why this component (not a second, differently-scoped notes system) is what belongs inside
// AssessmentCaptureCard — it's the one that actually satisfies that gate.
const NOTE_TYPES = ['measurement', 'inspection', 'site_observation', 'access', 'safety', 'special_instruction', 'customer_preference', 'general'];

export interface PropertyNoteSummaryLite {
  note_id: string;
  title?: string | null;
  note_type: string;
  source?: string | null;
  audio_url?: string | null;
  final_transcript?: string | null;
  clean_transcript?: string | null;
  review_summary?: string | null;
  created_on?: string | null;
}

function PropertyNoteRow({ note }: { note: PropertyNoteSummaryLite }) {
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const previewRef = useRef<HTMLParagraphElement>(null);
  const transcript = note.clean_transcript || note.final_transcript;
  const isVoice = note.source === 'voice' || !!note.audio_url;
  const preview = note.review_summary || transcript || note.title || '(empty note)';

  useEffect(() => {
    if (previewRef.current) {
      const el = previewRef.current;
      setIsClamped(el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth);
    }
  }, [preview]);

  return (
    <div className="rounded-lg border border-ui-border bg-ui-subtle px-3 py-2">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-micro font-bold px-2 py-0.5 rounded-full uppercase tracking-wide bg-accent/10 text-accent-text">
          {humanizeLabel(note.note_type)}
        </span>
        {isVoice && (
          <span
            className="flex-shrink-0 flex items-center gap-1 text-xs font-medium text-ui-text2"
            title="Voice note"
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
            Voice
          </span>
        )}
        {note.created_on && <span className="text-xs text-ui-text3 ml-auto">{fmtDate(note.created_on)}</span>}
      </div>
      <p ref={previewRef} className={`text-xs text-ui-text2 break-all${expanded ? ' whitespace-pre-wrap' : ' line-clamp-1'}`}>
        {preview}
      </p>
      {note.audio_url && (
        <div className="mt-2">
          <audio controls preload="none" src={note.audio_url} className="h-8 w-full">
            Your browser does not support audio playback.
          </audio>
        </div>
      )}
      {(isClamped || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="mt-1 text-xs font-medium text-accent-text hover:opacity-80"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

export function PropertyNotesSection({ propertyId, engagementId, notes, readOnly }: {
  propertyId: string;
  engagementId?: string;
  notes: PropertyNoteSummaryLite[];
  readOnly?: boolean;
}) {
  const [formOpen, setFormOpen] = useState(false);
  const [text, setText] = useState('');
  const [noteType, setNoteType] = useState('access');
  const [error, setError] = useState<string | null>(null);
  const [createNote, { isLoading }] = useCreatePropertyNoteMutation();

  async function submit() {
    if (!text.trim()) return;
    setError(null);
    try {
      await createNote({
        propertyId,
        body: {
          text_content: text.trim(),
          note_type: noteType,
          title: text.slice(0, 60),
          engagement_id: engagementId,
        },
      }).unwrap();
      setText('');
      setFormOpen(false);
    } catch {
      setError('Could not save the note — try again.');
    }
  }

  return (
    <CardSection
      title="Notes"
      count={notes.length}
      onAdd={readOnly ? undefined : () => setFormOpen(v => !v)}
      addLabel="Add Note"
      emptyText={notes.length === 0 && !formOpen
        ? readOnly ? "No notes yet. Add them from inside an engagement."
                   : "Gate codes, dog warnings, access instructions — save them here so every crew sees them."
        : undefined}
    >
      {formOpen && (
        <div className="rounded-lg border border-ui-border bg-ui-card p-3 flex flex-col gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {NOTE_TYPES.map(nt => (
              <button
                key={nt}
                type="button"
                onClick={() => setNoteType(nt)}
                className={`text-micro font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${noteType === nt ? 'bg-accent-fill text-on-accent' : 'bg-ui-subtle text-ui-text2 border border-ui-border'}`}
              >
                {humanizeLabel(nt)}
              </button>
            ))}
          </div>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="e.g. Gate code is 4321. Dog in backyard."
            rows={2}
            className="rounded-lg border border-ui-border bg-ui-subtle px-3 py-2 text-sm resize-none focus:outline-none focus:border-ui-ring"
          />
          {error && <p className="text-xs text-danger-text">{error}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setFormOpen(false)} className="text-xs px-3 py-1 rounded-full text-ui-text2 hover:bg-ui-hover">Cancel</button>
            <button type="button" disabled={!text.trim() || isLoading} onClick={submit} className="text-xs font-medium px-3 py-1 rounded-full btn-sheen text-on-primary disabled:opacity-40">
              {isLoading ? 'Saving…' : 'Save note'}
            </button>
          </div>
        </div>
      )}
      {notes.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {notes.slice(0, 5).map(n => (
            <PropertyNoteRow key={n.note_id} note={n} />
          ))}
          {notes.length > 5 && <p className="text-xs text-ui-text3">+{notes.length - 5} more</p>}
        </div>
      )}
    </CardSection>
  );
}

export default PropertyNotesSection;
